import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import moment from "moment";
import { useEffect, useState } from "react";
import { HandlingUnitType } from "@freightsimple/generated-dashboard-openapi-client/models/HandlingUnitType";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";

interface UseBookShipmentWarningMessagesProps {
  pickupDate: string;
  lineItems: AddLineItemType[];
  pickupLocation: AddLocationLocationType;
  deliveryLocation: AddLocationLocationType;
}

export function useBookShipmentWarningMessages({
  pickupDate,
  lineItems,
  pickupLocation,
  deliveryLocation,
}: UseBookShipmentWarningMessagesProps) {
  // Each warning is a group of message lines, rendered by
  // BookShipmentWarningMessages
  const [warnings, setWarnings] = useState<string[][]>([]);

  useEffect(() => {
    const momentPickupDate = moment(pickupDate);

    const newWarnings: string[][] = [];

    if (!momentPickupDate.weekday) {
      newWarnings.push([
        `This is a weekend pickup. Did you mean a different date?`,
      ]);
    }

    const suspiciouslySmallPallets = lineItems.some(function (lineItem) {
      return (
        lineItem.handlingUnitType === HandlingUnitType.Pallet &&
        lineItem.width &&
        lineItem.width < 35 &&
        lineItem.height &&
        lineItem.height < 35
      );
    });

    if (suspiciouslySmallPallets) {
      newWarnings.push([
        `One of your pallets seems much smaller than standard size. Make sure to include the pallet in your measurements.`,
      ]);
    }

    if (moment(pickupDate).subtract(4, "days").isAfter(moment())) {
      newWarnings.push([
        `For some lanes, the best rates are only available if pickup is within 3-4 days from today`,
      ]);
    }

    if (
      pickupLocation.locationType === LocationType.Residential &&
      !pickupLocation.accessorials.includes("LOGISTICS_LIFT_GATE_REQUIRED")
    ) {
      newWarnings.push([
        `Most residential pickups require a lift gate. Please double-check you do not need one.`,
      ]);
    }

    if (
      deliveryLocation.locationType === LocationType.Residential &&
      !deliveryLocation.accessorials.includes("LOGISTICS_LIFT_GATE_REQUIRED")
    ) {
      newWarnings.push([
        `Most residential deliveries require a lift gate. Please double-check you do not need one.`,
      ]);
    }

    if (
      deliveryLocation.locationType === LocationType.Residential &&
      !deliveryLocation.accessorials.includes("SCHEDULING_APPOINTMENT_REQUIRED")
    ) {
      newWarnings.push([
        `Many residential deliveries require an appointment. Please double-check you do not need one.`,
      ]);
    }

    function checkLotsOfBoxes(lineItem: AddLineItemType): boolean {
      return (
        lineItem.handlingUnitType === HandlingUnitType.Box &&
        lineItem.numberHandlingUnits > 5
      );
    }

    if (lineItems.some((li) => checkLotsOfBoxes(li))) {
      newWarnings.push([
        "This seems like a lot of boxes. They should probably be assembled onto a pallet for proper shipping",
      ]);
    }

    setWarnings(newWarnings);
  }, [pickupDate, lineItems, pickupLocation, deliveryLocation]);

  return { warnings, setWarnings };
}
